import { Code, ConnectError } from '@connectrpc/connect';
import useAuthStore from '../stores/auth.store';
import { authClient } from './api-grpc';

export class InvalidCredentialsError extends Error {
  constructor() {
    super('Invalid credentials');
    this.name = 'InvalidCredentialsError';
  }
}

// A refresh triggered by several requests failing at once must only hit the API once, because Keycloak rotates the
// refresh token and the second call would present an already used one.
let refreshInFlight: Promise<string | undefined> | undefined;

export const authApi = {
  async login(email: string, password: string): Promise<{ accessToken: string; refreshToken: string }> {
    try {
      const response = await authClient.login({ email, password });
      return { accessToken: response.accessToken, refreshToken: response.refreshToken };
    } catch (error) {
      if (
        error instanceof ConnectError &&
        [Code.Unauthenticated, Code.InvalidArgument, Code.NotFound, Code.PermissionDenied].includes(error.code)
      ) {
        throw new InvalidCredentialsError();
      }
      throw error;
    }
  },

  async logout(refreshToken: string): Promise<void> {
    try {
      await authClient.logout({ refreshToken });
    } catch {
      // The local session is cleared regardless; an expired refresh token is not worth surfacing.
    }
  },

  refreshToken(): Promise<string | undefined> {
    if (!refreshInFlight) {
      refreshInFlight = (async () => {
        const { refreshToken, setTokens, clearTokens } = useAuthStore.getState();
        if (!refreshToken) return undefined;
        try {
          const response = await authClient.refreshToken({ refreshToken });
          setTokens(response.accessToken, response.refreshToken);
          return response.accessToken;
        } catch {
          clearTokens();
          return undefined;
        }
      })().finally(() => {
        refreshInFlight = undefined;
      });
    }
    return refreshInFlight;
  },
};
